import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Download, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import Loader from '@/components/Loader';
import { reportService } from '@/services';
import { download } from '@/utils/format';

export default function Reports() {
  const token = useSelector((s) => s.auth.token);
  const [range, setRange] = useState({
    from: dayjs().subtract(29, 'day').format('YYYY-MM-DD'),
    to: dayjs().format('YYYY-MM-DD'),
  });
  const { data, isLoading } = useQuery({
    queryKey: ['report', 'performance', range],
    queryFn: () => reportService.performance(range),
  });

  const rows = data?.items || [];
  const chartData = rows.slice(0, 10).map((r) => ({
    name: (r.user?.name || '—').split(' ')[0],
    attended: r.attendanceHours,
    reported: r.reportedHours,
  }));

  const exportCSV = (kind) => {
    const qp = new URLSearchParams(range).toString();
    if (kind === 'attendance') download(`reports/admin/attendance.csv?${qp}`, `attendance_${range.from}_${range.to}.csv`, token);
    if (kind === 'work') download(`reports/admin/work-reports.csv?${qp}`, `work_reports_${range.from}_${range.to}.csv`, token);
    if (kind === 'leaves') download('reports/admin/leaves.csv', 'leaves.csv', token);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reports & Analytics"
        subtitle="Employee performance and downloadable reports"
        actions={
          <div className="flex gap-2 flex-wrap">
            <button className="btn-secondary" onClick={() => exportCSV('attendance')}><Download size={14} /> Attendance CSV</button>
            <button className="btn-secondary" onClick={() => exportCSV('work')}><Download size={14} /> Work reports CSV</button>
            <button className="btn-secondary" onClick={() => exportCSV('leaves')}><Download size={14} /> Leaves CSV</button>
          </div>
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div><label className="label">From</label><input type="date" className="input w-44" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} /></div>
          <div><label className="label">To</label><input type="date" className="input w-44" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} /></div>
        </div>

        {isLoading ? (
          <Loader />
        ) : (
          <>
            <div className="font-semibold mb-3 flex items-center gap-2"><BarChart3 size={16} /> Attended vs reported hours (top 10)</div>
            <div className="h-72 mb-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="attended" fill="#3a64ff" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="reported" fill="#10b981" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="table-wrap">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className="th">Employee</th>
                    <th className="th">Department</th>
                    <th className="th">Attended Days</th>
                    <th className="th">Attended Hours</th>
                    <th className="th">Late</th>
                    <th className="th">Reported Hours</th>
                    <th className="th">Reports</th>
                    <th className="th">Tasks Done</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={8} className="td text-center text-slate-500">No data</td></tr>}
                  {rows.map((r) => (
                    <tr key={r.user?._id}>
                      <td className="td font-medium">{r.user?.name || '—'}</td>
                      <td className="td">{r.user?.department || '—'}</td>
                      <td className="td">{r.attendanceDays}</td>
                      <td className="td">{r.attendanceHours} h</td>
                      <td className="td">{r.lateCount}</td>
                      <td className="td">{r.reportedHours} h</td>
                      <td className="td">{r.reports}</td>
                      <td className="td">{r.tasksCompleted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
